import { logger } from "./Logger";

interface Milestone {
  name: string;
  timestamp: number;
  duration?: number; // Duration since previous milestone in seconds
}

interface ServerTiming {
  totalServerDuration: number;
  aiProviderDuration: number;
  validationTime: number;
  controllerStartTime: number;
  aiProviderCallStart: number;
  aiProviderCallEnd: number;
  steps: Array<{ name: string; duration: number; timestamp: number }>;
}

interface ProviderTiming {
  totalDuration: number;
  messagePreprocessingStart: number;
  messagePreprocessingEnd: number;
  apiCallStart: number;
  apiCallEnd: number;
  steps: Array<{ name: string; duration: number; timestamp: number }>;
}

interface DurationSession {
  id: string;
  name: string;
  startTime: number;
  milestones: Milestone[];
  isCompleted: boolean;
  serverTiming?: ServerTiming;
  providerTiming?: ProviderTiming;
  usageInfo?: {
    modelName?: string;
    promptTokenCount?: number;
    candidatesTokenCount?: number;
    totalTokenCount?: number;
    costUSD?: number;
    pricingModelUsed?: any;
    costCalculationError?: string;
  };
}

/**
 * DurationTracker class for measuring and logging durations across the app
 * Supports multiple concurrent sessions and milestone tracking
 */
export class DurationTracker {
  private sessions: Map<string, DurationSession> = new Map();

  /**
   * Start a new duration tracking session
   * @param sessionId - Unique identifier for the session
   * @param sessionName - Descriptive name for logging
   * @returns The start timestamp
   */
  startSession(sessionId: string, sessionName: string): number {
    const startTime = Date.now();

    const session: DurationSession = {
      id: sessionId,
      name: sessionName,
      startTime,
      milestones: [],
      isCompleted: false,
    };

    this.sessions.set(sessionId, session);

    logger.debug.info(
      `DurationTracker: Started session "${sessionName}" (${sessionId})`
    );

    return startTime;
  }

  /**
   * Add a milestone to an existing session
   * @param sessionId - Session identifier
   * @param milestoneName - Descriptive name for the milestone
   * @returns The timestamp and duration since last milestone/start
   */
  addMilestone(
    sessionId: string,
    milestoneName: string
  ): { timestamp: number; duration: number } | null {
    const session = this.sessions.get(sessionId);

    if (!session) {
      logger.warn(`DurationTracker: Session "${sessionId}" not found`);
      return null;
    }

    if (session.isCompleted) {
      logger.warn(
        `DurationTracker: Session "${sessionId}" is already completed`
      );
      return null;
    }

    const timestamp = Date.now();
    const lastTime =
      session.milestones.length > 0
        ? session.milestones[session.milestones.length - 1].timestamp
        : session.startTime;

    const duration = (timestamp - lastTime) / 1000; // Convert to seconds

    const milestone: Milestone = {
      name: milestoneName,
      timestamp,
      duration,
    };

    session.milestones.push(milestone);

    logger.debug.info(
      `DurationTracker: Milestone "${milestoneName}" in session "${
        session.name
      }" - ${duration.toFixed(2)}s since last milestone`
    );

    return { timestamp, duration };
  }

  /**
   * Complete a session and log the full summary
   * @param sessionId - Session identifier
   * @param finalMilestoneName - Optional final milestone name
   * @returns Summary of the session including server, provider timing, and usage info
   */
  completeSession(
    sessionId: string,
    finalMilestoneName?: string
  ): {
    totalDuration: number;
    milestones: Milestone[];
    summary: string;
    serverTiming?: ServerTiming;
    providerTiming?: ProviderTiming;
    usageInfo?: {
      modelName?: string;
      promptTokenCount?: number;
      candidatesTokenCount?: number;
      totalTokenCount?: number;
      costUSD?: number;
      pricingModelUsed?: any;
      costCalculationError?: string;
    };
  } | null {
    const session = this.sessions.get(sessionId);

    if (!session) {
      logger.warn(`DurationTracker: Session "${sessionId}" not found`);
      return null;
    }

    if (session.isCompleted) {
      logger.warn(
        `DurationTracker: Session "${sessionId}" is already completed`
      );
      return null;
    }

    // Add final milestone if provided
    if (finalMilestoneName) {
      this.addMilestone(sessionId, finalMilestoneName);
    }

    const endTime = Date.now();
    const totalDuration = (endTime - session.startTime) / 1000; // Convert to seconds
    session.isCompleted = true;

    // Create summary
    let summary = `DurationTracker Summary for "${session.name}":\n`;
    summary += `Total Duration: ${totalDuration.toFixed(2)}s\n`;

    if (session.milestones.length > 0) {
      summary += `Milestones:\n`;
      session.milestones.forEach((milestone, index) => {
        summary += `  ${index + 1}. ${
          milestone.name
        }: +${milestone.duration?.toFixed(2)}s\n`;
      });
    }

    // Add server timing to summary if available
    if (session.serverTiming) {
      summary += `\nServer Timing:\n`;
      summary += `  Total Server Duration: ${session.serverTiming.totalServerDuration.toFixed(
        2
      )}s\n`;
      summary += `  AI Provider Duration: ${session.serverTiming.aiProviderDuration.toFixed(
        2
      )}s\n`;
      summary += `  Validation Time: ${session.serverTiming.validationTime.toFixed(
        2
      )}s\n`;
    }

    // Add provider timing to summary if available
    if (session.providerTiming) {
      summary += `\nProvider Timing:\n`;
      summary += `  Total Provider Duration: ${session.providerTiming.totalDuration.toFixed(
        2
      )}s\n`;
      const preprocessingDuration =
        (session.providerTiming.messagePreprocessingEnd -
          session.providerTiming.messagePreprocessingStart) /
        1000;
      const apiCallDuration =
        (session.providerTiming.apiCallEnd -
          session.providerTiming.apiCallStart) /
        1000;
      summary += `  Message Preprocessing: ${preprocessingDuration.toFixed(
        2
      )}s\n`;
      summary += `  API Call Duration: ${apiCallDuration.toFixed(2)}s\n`;
    }

    logger.info(summary);

    // Clean up the session after logging
    setTimeout(() => {
      this.sessions.delete(sessionId);
    }, 1000);

    return {
      totalDuration,
      milestones: session.milestones,
      summary,
      serverTiming: session.serverTiming,
      providerTiming: session.providerTiming,
      usageInfo: session.usageInfo,
    };
  }

  /**
   * Get current duration for an active session
   * @param sessionId - Session identifier
   * @returns Current duration in seconds
   */
  getCurrentDuration(sessionId: string): number | null {
    const session = this.sessions.get(sessionId);

    if (!session) {
      return null;
    }

    return (Date.now() - session.startTime) / 1000;
  }

  /**
   * Cancel and remove a session without logging summary
   * @param sessionId - Session identifier
   */
  cancelSession(sessionId: string): void {
    const session = this.sessions.get(sessionId);

    if (session) {
      logger.debug.info(
        `DurationTracker: Cancelled session "${session.name}" (${sessionId})`
      );
      this.sessions.delete(sessionId);
    }
  }

  /**
   * Get session information
   * @param sessionId - Session identifier
   * @returns Session info or null if not found
   */
  getSession(sessionId: string): DurationSession | null {
    return this.sessions.get(sessionId) || null;
  }

  /**
   * Get all active session IDs
   * @returns Array of active session IDs
   */
  getActiveSessions(): string[] {
    return Array.from(this.sessions.keys()).filter(
      (sessionId) => !this.sessions.get(sessionId)?.isCompleted
    );
  }

  /**
   * Clear all sessions (useful for cleanup)
   */
  clearAllSessions(): void {
    this.sessions.clear();
    logger.debug.info("DurationTracker: Cleared all sessions");
  }

  /**
   * Add server timing data to an existing session
   * @param sessionId - Session identifier
   * @param serverTiming - Server timing data from the API response
   */
  addServerTiming(sessionId: string, serverTiming: any): void {
    const session = this.sessions.get(sessionId);
    if (!session) {
      logger.warn(
        `DurationTracker: Session "${sessionId}" not found for server timing`
      );
      return;
    }

    // Convert timestamps from milliseconds to seconds for consistency
    session.serverTiming = {
      totalServerDuration: serverTiming.totalServerDuration / 1000,
      aiProviderDuration: serverTiming.aiProviderDuration / 1000,
      validationTime: serverTiming.validationTime / 1000,
      controllerStartTime: serverTiming.controllerStartTime,
      aiProviderCallStart: serverTiming.aiProviderCallStart,
      aiProviderCallEnd: serverTiming.aiProviderCallEnd,
      steps: serverTiming.steps.map((step: any) => ({
        name: step.name,
        duration: step.duration / 1000, // Convert to seconds
        timestamp: step.timestamp,
      })),
    };

    logger.debug.info(
      `DurationTracker: Added server timing to session "${
        session.name
      }" - ${session.serverTiming.totalServerDuration.toFixed(2)}s total`
    );
  }

  /**
   * Add provider timing data to an existing session
   * @param sessionId - Session identifier
   * @param providerTiming - Provider timing data from the API response
   */
  addProviderTiming(sessionId: string, providerTiming: any): void {
    const session = this.sessions.get(sessionId);
    if (!session) {
      logger.warn(
        `DurationTracker: Session "${sessionId}" not found for provider timing`
      );
      return;
    }

    // Convert timestamps from milliseconds to seconds for consistency
    session.providerTiming = {
      totalDuration: providerTiming.totalDuration / 1000,
      messagePreprocessingStart: providerTiming.messagePreprocessingStart,
      messagePreprocessingEnd: providerTiming.messagePreprocessingEnd,
      apiCallStart: providerTiming.apiCallStart,
      apiCallEnd: providerTiming.apiCallEnd,
      steps: providerTiming.steps.map((step: any) => ({
        name: step.name,
        duration: step.duration / 1000, // Convert to seconds
        timestamp: step.timestamp,
      })),
    };

    logger.debug.info(
      `DurationTracker: Added provider timing to session "${
        session.name
      }" - ${session.providerTiming.totalDuration.toFixed(2)}s total`
    );
  }

  /**
   * Add usage information to an existing session
   * @param sessionId - Session identifier
   * @param usageInfo - Usage information from the AI provider response
   */
  addUsageInfo(sessionId: string, usageInfo: any): void {
    const session = this.sessions.get(sessionId);
    if (!session) {
      logger.warn(
        `DurationTracker: Session "${sessionId}" not found for usage info`
      );
      return;
    }

    session.usageInfo = {
      modelName: usageInfo.modelName,
      promptTokenCount: usageInfo.promptTokenCount,
      candidatesTokenCount: usageInfo.candidatesTokenCount,
      totalTokenCount: usageInfo.totalTokenCount,
      costUSD: usageInfo.costUSD,
      pricingModelUsed: usageInfo.pricingModelUsed,
      costCalculationError: usageInfo.costCalculationError,
    };

    logger.debug.info(
      `DurationTracker: Added usage info to session "${session.name}" - ${
        session.usageInfo.totalTokenCount || 0
      } tokens, $${session.usageInfo.costUSD?.toFixed(4) || "unknown"} USD`
    );
  }
}

// Export a singleton instance for global use
export const durationTracker = new DurationTracker();
